import { db } from '../../config/main_config.js';
import { getEnabledModules, standardNameToModuleKey } from './EnabledModulesManager';

const ENABLED_MODULES_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let cachedEnabledModules = null;

const buildNormalizedModuleForms = (rawValue) => {
    const forms = new Set();
    if (!rawValue || typeof rawValue !== 'string') {
        return forms;
    }

    const normalizedBase = (standardNameToModuleKey(rawValue) || rawValue).trim().toLowerCase();

    if (!normalizedBase) {
        return forms;
    }

    const noSpaces = normalizedBase.replace(/\s+/g, '');
    const noPunctuation = noSpaces.replace(/[-_]+/g, '');

    forms.add(normalizedBase);
    forms.add(noSpaces);
    forms.add(noPunctuation);

    return forms;
};

const getNormalizedEnabledModules = async () => {
    const now = Date.now();
    if (!cachedEnabledModules || (now - cachedEnabledModules.fetchedAt) > ENABLED_MODULES_CACHE_TTL) {
        try {
            const enabledModulesRaw = await getEnabledModules();
            const normalizedSet = new Set();

            enabledModulesRaw.forEach((moduleKey) => {
                buildNormalizedModuleForms(moduleKey).forEach((form) => {
                    if (form) {
                        normalizedSet.add(form);
                    }
                });
            });

            cachedEnabledModules = {
                modules: normalizedSet,
                fetchedAt: now,
            };
        } catch (error) {
            console.error('[findFrameworkName] Failed to load enabled modules:', error);
            cachedEnabledModules = {
                modules: new Set(),
                fetchedAt: now,
            };
        }
    }

    return cachedEnabledModules.modules;
};

const isModuleEnabled = async (docId, friendlyName) => {
    const enabledModules = await getNormalizedEnabledModules();

    // If we couldn't determine enabled modules, allow by default to avoid blocking UX
    if (!enabledModules || enabledModules.size === 0) {
        return true;
    }

    const formsToCheck = new Set();
    buildNormalizedModuleForms(docId).forEach((form) => formsToCheck.add(form));
    buildNormalizedModuleForms(friendlyName).forEach((form) => formsToCheck.add(form));

    for (const form of formsToCheck) {
        if (enabledModules.has(form)) {
            return true;
        }
    }

    return false;
};

export async function findFrameworkName(controlID, standardToFramework = false, returnRawStandardDocName = false) {
    const complianceCollection = db.collection('compliance');

    const docIdToFriendlyName = {
        'iso270012013AnnexAControls': 'ISO 27001:2013',
        'ACSCE8MM112022IDControls': 'Essential 8',
        'ACSCE8MM102021IDControls': 'Essential 8',
        'DISPControls': 'DISP',
        'E8 Controls': 'Essential 8',
        'E8Controls': 'Essential 8',
        'SOC2Controls': 'SOC 2',
        'iso270012022AnnexAControls': 'ISO 27001:2022',
        'iso270012022Controls': 'ISO 27001:2022',
        'nist-800-171rev2': 'NIST 800-171',
        'nist-csf-rev1.1': 'NIST CSF',
        'nist800-53-rev5': 'NIST 800-53',
        'paControls': 'Privacy Act',
        'ism': "ISM",
        "iso420012023Controls": 'ISO 42001',
        "smb1001controls": "SMB1001",
        "SMB1001Controls": "SMB 1001",
        "ISO 27701": "ISO 27701",
        "ISO27701": "ISO 27701",
        // Add more mappings here if needed
    };

    if (standardToFramework) {
        const friendlyName = docIdToFriendlyName[controlID] || controlID;
        if (await isModuleEnabled(controlID, friendlyName)) {
            return friendlyName;
        }
        return undefined;
    }

    if (returnRawStandardDocName) {
        let standardName = controlID;
        const friendlyName = await findFrameworkName(controlID);
        for (const standardDocName of Object.keys(docIdToFriendlyName)) {
            if (docIdToFriendlyName[standardDocName] === friendlyName) {
                standardName = standardDocName
                break;
            }
        }
        return standardName;
    }

    // Known subcollection names
    const knownSubcollections = ['controls']; // Replace with actual subcollection names if different

    try {
        const snapshot = await complianceCollection.get();

        // First, check for matches at the root level across all documents
        for (const doc of snapshot.docs) {
            const data = doc.data();
            if (searchRootForControlID(data, controlID)) {
                const friendlyName = docIdToFriendlyName[doc.id] || doc.id;
                if (await isModuleEnabled(doc.id, friendlyName)) {
                    console.log("Found framework for control at root level:", friendlyName, controlID);
                    return friendlyName;
                }
            }
        }

        // If not found at root level, proceed to search recursively
        for (const doc of snapshot.docs) {
            const data = doc.data();

            // Check subcollections for document IDs matching controlID
            for (const subcollectionName of knownSubcollections) {
                const subcollectionRef = doc.ref.collection(subcollectionName);
                const subSnapshot = await subcollectionRef.get();

                for (const subDoc of subSnapshot.docs) {
                    if (subDoc.id === controlID) {
                        const friendlyName = docIdToFriendlyName[doc.id] || doc.id;
                        if (await isModuleEnabled(doc.id, friendlyName)) {
                            console.log("Found controlID in subcollection document ID:", friendlyName, controlID);
                            return friendlyName;
                        }
                    }
                }
            }

            if (searchDataForControlID(data, controlID)) {
                const friendlyName = docIdToFriendlyName[doc.id] || doc.id;
                if (await isModuleEnabled(doc.id, friendlyName)) {
                    console.log("Found framework for control recursively:", friendlyName, controlID);
                    return friendlyName;
                }
            }
        }

        return undefined;
    } catch (error) {
        console.error("Error fetching documents:", error);
        return undefined;
    }
}

function searchRootForControlID(data, controlID) {
    if (data == null || typeof data !== 'object') return false;

    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            const value = data[key];
            // Check if the key or value at the root level matches the controlID
            if (key === controlID || value === controlID) {
                return true;
            }
        }
    }
    return false;
}

function searchDataForControlID(data, controlID) {
    if (data == null) return false;

    if (Array.isArray(data)) {
        for (const item of data) {
            if (item === controlID) {
                return true;
            }
            if (typeof item === 'object' && item !== null) {
                if (searchDataForControlID(item, controlID)) {
                    return true;
                }
            }
        }
    } else if (typeof data === 'object') {
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const value = data[key];

                // Skip root level check since it's already done
                if (typeof value === 'object' && value !== null) {
                    if (searchDataForControlID(value, controlID)) {
                        return true;
                    }
                } else {
                    if (value === controlID) {
                        return true;
                    }
                }
            }
        }
    } else {
        if (data === controlID) {
            return true;
        }
    }
    return false;
}